/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, Users, FileText, UserCircle2, CornerDownLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

/** Escape regex meta-chars in user-typed query */
const escapeReg = (s) => (s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Highlight occurrences of q (case-insensitive) inside text */
function Highlight({ text, q }) {
  if (!text) return null;
  if (!q || q.length < MIN_CHARS) return <>{text}</>;
  const re = new RegExp(`(${escapeReg(q)})`, "ig");
  const parts = String(text).split(re);
  const ql = q.toLowerCase();
  return (
    <>
      {parts.map((p, i) =>
        p && p.toLowerCase() === ql ? (
          <mark
            key={i}
            className="bg-amber-200 text-slate-900 rounded-sm px-0.5"
          >
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

const GROUPS = [
  { key: "clients", label: "Clienți", icon: Users },
  { key: "contracts", label: "Contracte", icon: FileText },
  { key: "agents", label: "Agenți", icon: UserCircle2 },
];

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ clients: [], contracts: [], agents: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const reqIdRef = useRef(0);
  const debounceRef = useRef(null);

  // Flatten results in display order for keyboard nav
  const flatResults = useMemo(() => {
    const out = [];
    GROUPS.forEach((g) => {
      (results[g.key] || []).forEach((item) => {
        out.push({ ...item, _group: g.key });
      });
    });
    return out;
  }, [results]);

  const totalCount = flatResults.length;

  // Ctrl+K / Cmd+K hotkey
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === "k" || e.key === "K";
      if (isK && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
      if (e.key === "Escape" && document.activeElement === inputRef.current) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click outside closes dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < MIN_CHARS) {
      setResults({ clients: [], contracts: [], agents: [] });
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/search/global`, {
          params: { q: trimmed, limit: 5 },
        });
        // Drop stale responses
        if (myReq !== reqIdRef.current) return;
        setResults({
          clients: res.data.clients || [],
          contracts: res.data.contracts || [],
          agents: res.data.agents || [],
        });
        setActiveIdx(0);
      } catch (e) {
        if (myReq === reqIdRef.current) {
          setResults({ clients: [], contracts: [], agents: [] });
        }
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const goTo = useCallback(
    (item) => {
      if (!item?.url) return;
      setOpen(false);
      setQ("");
      setResults({ clients: [], contracts: [], agents: [] });
      navigate(item.url);
    },
    [navigate]
  );

  const onInputKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" && totalCount > 0) {
        setOpen(true);
        return;
      }
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(totalCount - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const item = flatResults[activeIdx];
      if (item) {
        e.preventDefault();
        goTo(item);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown =
    open && q.trim().length >= MIN_CHARS;

  // Detect platform for kbd hint
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPod|iPad/i.test(navigator.platform || "");

  let runningIdx = -1;

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[520px]"
      data-testid="global-search"
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          placeholder="Caută client, contract, agent, CNP, CUI, POD…"
          className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-20 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-[#1e3a8a] focus:ring-2 focus:ring-[#1e3a8a]/15"
          data-testid="global-search-input"
          autoComplete="off"
          spellCheck={false}
          aria-label="Căutare globală"
        />
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading ? (
            <Loader2 className="h-4 w-4 text-slate-400 animate-spin" data-testid="global-search-loading" />
          ) : (
            <kbd className="hidden sm:inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              {isMac ? "⌘" : "Ctrl"} + K
            </kbd>
          )}
        </div>
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-2 min-w-[420px] w-[min(640px,calc(100vw-3rem))] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          data-testid="global-search-dropdown"
          role="listbox"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500">
            <span>
              {totalCount > 0
                ? `${totalCount} rezultat${totalCount === 1 ? "" : "e"} pentru “${q.trim()}”`
                : loading
                  ? "Se caută…"
                  : "Niciun rezultat"}
            </span>
            <span className="hidden sm:flex items-center gap-2">
              <kbd className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px]">↑↓</kbd>
              <kbd className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px]">Enter</kbd>
              <kbd className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px]">Esc</kbd>
            </span>
          </div>

          {/* Body */}
          <div className="max-h-[60vh] overflow-y-auto py-1">
            {totalCount === 0 && !loading && (
              <div
                className="px-4 py-8 text-center text-sm text-slate-500"
                data-testid="global-search-empty"
              >
                Niciun rezultat pentru <span className="font-medium text-slate-700">“{q.trim()}”</span>.
                <div className="mt-1 text-xs text-slate-400">
                  Încearcă numele complet, CNP/CUI, POD/NLC sau codul Marca al agentului.
                </div>
              </div>
            )}

            {GROUPS.map((g) => {
              const items = results[g.key] || [];
              if (items.length === 0) return null;
              const Icon = g.icon;
              return (
                <div key={g.key} data-testid={`global-search-group-${g.key}`}>
                  <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <Icon className="h-3.5 w-3.5" />
                    <span>{g.label}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-400">{items.length}</span>
                  </div>
                  {items.map((item) => {
                    runningIdx += 1;
                    const isActive = runningIdx === activeIdx;
                    return (
                      <button
                        key={`${g.key}-${item.id}`}
                        type="button"
                        onMouseEnter={() => setActiveIdx(runningIdx)}
                        onClick={() => goTo(item)}
                        className={`group flex w-full items-start gap-3 px-3 py-2 text-left transition-colors ${
                          isActive
                            ? "bg-[#1e3a8a]/8"
                            : "hover:bg-slate-50"
                        }`}
                        data-testid={`global-search-result-${g.key}-${item.id}`}
                      >
                        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-slate-900">
                            <Highlight text={item.label} q={q.trim()} />
                            {g.key === "agents" && item.active === false && (
                              <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-normal text-slate-600">
                                inactiv
                              </span>
                            )}
                          </div>
                          {item.subtitle && (
                            <div className="truncate text-xs text-slate-500">
                              <Highlight text={item.subtitle} q={q.trim()} />
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <CornerDownLeft className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
